import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import { getClipConsentStatus } from "../../api/socialClipApi";
import { CONSENT_PURPOSES } from "../../constants/legalPolicy";
import { getLiveTrainingPortalRoot } from "../../hooks/useLiveFullscreen";
import "./FilmingConsentBar.css";

export function FilmingConsentBar({ teId, tableId = null }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!teId) return undefined;
    let cancelled = false;
    getClipConsentStatus({
      teId,
      tableId,
      purpose: CONSENT_PURPOSES.TRAINING_ANALYSIS,
    })
      .then((data) => {
        if (!cancelled) setStatus(data);
      })
      .catch(() => {
        if (!cancelled) setStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [teId, tableId]);

  if (!teId || !status) return null;

  const missing = (status.subjects || []).filter((s) => !s.granted);
  const reason = String(status.reason || "");
  // No seated subjects yet (demo walk-in lag) is not a consent refusal — hide.
  if (!status.allowed && (reason === "no_subjects_resolved" || !missing.length)) {
    return null;
  }
  // Informational only: filming remains allowed for players who are cleared.
  if (!missing.length) return null;

  const names = missing.map((s) => s.display_name || s.user_id).join(", ");
  const message = t(
    "filmingConsentPartial",
    "Video consent missing for: {{names}}. Cleared players can still be recorded.",
    { names }
  );

  const portalRoot = getLiveTrainingPortalRoot();
  const dialog =
    open && portalRoot
      ? createPortal(
          <div
            className="filming-consent-dialog__backdrop"
            role="presentation"
            onClick={() => setOpen(false)}
          >
            <div
              className="filming-consent-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="filming-consent-dialog-title"
              onClick={(e) => e.stopPropagation()}
            >
              <header className="filming-consent-dialog__header">
                <h3 id="filming-consent-dialog-title">
                  {t("filmingConsentTitle", "Video consent")}
                </h3>
                <button
                  type="button"
                  className="filming-consent-dialog__close"
                  onClick={() => setOpen(false)}
                  aria-label={t("close", "Close")}
                >
                  <CloseIcon fontSize="small" />
                </button>
              </header>
              <p className="filming-consent-dialog__body">{message}</p>
              <button
                type="button"
                className="filming-consent-dialog__ok"
                onClick={() => setOpen(false)}
              >
                {t("ok", "OK")}
              </button>
            </div>
          </div>,
          portalRoot
        )
      : null;

  return (
    <>
      <button
        type="button"
        className="filming-consent-icon"
        onClick={() => setOpen(true)}
        aria-label={t("filmingConsentTitle", "Video consent")}
        title={message}
      >
        <WarningAmberIcon fontSize="small" />
        <span className="filming-consent-icon__count">{missing.length}</span>
      </button>
      {dialog}
    </>
  );
}

export default FilmingConsentBar;
